import React, { ReactNode, useEffect, useState, useMemo, FunctionComponent } from 'react';
import { BrowserRouter, Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import './App.scss';
import 'bootstrap/dist/css/bootstrap.css';
import { themes, styles } from './styles/themes/style';
import styled, { ThemeProvider } from 'styled-components';
import { analytic, getPageTitle, getPageUrl } from './utils';
import { AppRoutes, ISidebar, SidebarTypes } from './interfaces/store/appStore';
import { useAppStore } from './stores/application/useAppStore';
import { useAuthStore } from './stores/auth/useAuthStore';
import { useAppHandlers } from './services/actions/app/useAppHandlers';
import { useAppActions } from './stores/application/useAppActions';
import { useExternalHandlers } from './services/actions/navigation/useExternalHandlers';
import Reset from './styles/Reset';
import Header from './components/Header';
import AppModal from './components/modal/AppModal';
import AppRouter from './wrappers/AppRouter';
import Toast from './components/Toast';
import Spinner from './components/Spinner';
import AppContainer from './pages/AppContainer/AppContainer';
import Sidebar from './components/Sidebar';
import { SideNav } from './components/SideNav';
import SidebarRender from './fragments/SidebarRender';
import CrispChat from './fragments/CrispChat';
import SvgFragment from './fragments/SvgFragment';
import Text from './components/Text';
import ActionText from './components/ActionText';
import Row from './components/Row';
import LoadingSpinner from './components/LoadingSpinner';
import Banner from './components/Banner'
import Column from './components/Column';
import DemoVideo from './pages/canvas/components/home/DemoVideo';
import ConnectLinkedInProfileModal from './components/modal/modals/ConnectLinkedInProfileModal';
import posthog from 'posthog-js';
import CountdownPopup from './fragments/CountdownPromoPopup';
import BannerCountdown from './fragments/BannerCountdown';
import ChatWindows from './components/chat/ChatPopups';
import { useExtensionRequest } from './services/apis/extension/useExtensionRequest';
import BlackFridayBanner from './fragments/BlackFridayBanner';
import EOYbanner from './fragments/EOYbanner';
import PaymentVerificationBanner from './fragments/PaymentVerificationBanner';
import PaymentVerificationHandler from './wrappers/PaymentVerificationHandler';
import { crispHide, crispIdentify, crispReset, crispShow, loadCrisp } from './services/crisp/crisp';
import Maintenance from './pages/Maintenance';
import CardExpiryBanner from './fragments/CardExpiryBanner';
import { getCardExpiryBannerState, mergePaymentMethodsForBanner } from './utils/cardExpiry';
import { InboxStoreProvider } from './stores/inbox/useInboxStore'
import WebUpdateBanner from './components/WebUpdateBanner';

const MAINTENANCE_MODE = (() => {
  const raw = process.env.REACT_APP_MAINTENANCE_MODE;
  if (!raw) return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
})();

const MaintenanceShell: FunctionComponent = () => (
  <ThemeProvider theme={themes.light}>
    <Reset />
    <Maintenance />
  </ThemeProvider>
);

function AppMain() {
  const { store: { user, trial, subscription, disablePremiumFeatures, workspaceOwner, workspaceAdmin, workspace, lifetime } } = useAuthStore()
  const { store: { spinner, extensionId, sidebar, darkMode, extensionInstalled, extensionVersion, onboardingStep, leadDeltaTabIsActive } } = useAppStore()
  const { checkExtensionStatus, closeSidebar, hideModal, setLeadDeltaTabIsActiveHandler } = useAppHandlers()
  const { openSyncMessage, updateLeadDeltaExtension, scheduleMeeting } = useExternalHandlers()
  const { setGraphqlQueryIds } = useExtensionRequest()
  const [url, setUrl] = React.useState(window.location.href);
  const [crispChat, setCrispChat] = React.useState(false);
  const [hideDemoVideo, setHideDemoVideo] = useState<boolean>(true);
  const [showPaymentVerificationBanner, setShowPaymentVerificationBanner] = useState<boolean>(false);
  const [paymentVerificationData, setPaymentVerificationData] = useState<any>(null);
  const pathname = React.useMemo(() => {
    if (!url) return undefined;
    try {
      return new URL(url).pathname;
    } catch {
      return undefined;
    }
  }, [url]);
  const isBilling = pathname?.startsWith(AppRoutes.BILLING);
  const isHome = pathname === AppRoutes.HOME;
  const isConnections = pathname === AppRoutes.CONNECTIONS;
  const isDisabled = pathname?.startsWith(AppRoutes.DISABLED);
  const isOnboarding = pathname?.startsWith(AppRoutes.ONBOARDING) || pathname === AppRoutes.PROCESSING;

  useEffect(() => {
    if (!user) {
      setHideDemoVideo(true)
      return
    }
    setHideDemoVideo(user.preferences?.hideDemoVideo ?? false)
  }, [user?._id, user?.preferences?.hideDemoVideo])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setLeadDeltaTabIsActiveHandler(false);
      } else if (document.visibilityState === "visible" && !leadDeltaTabIsActive) {
        console.log("Tab is active again.");
        setLeadDeltaTabIsActiveHandler(true); // Update state to reflect that the tab is now active
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [leadDeltaTabIsActive]); // Depend on leadDeltaTabIsActive to re-run the effect when it changes

  const refs = {
    previousUrl: React.useRef<any>(''),
    url: React.useRef<any>(window.location.href)
  };

  useEffect(() => {
    loadCrisp();
  }, [])

  useEffect(() => {
    const observer = new MutationObserver(async (mutations) => {
      mutations.forEach(async (mutation) => {
        if (refs.url.current !== window.location.href) {
          refs.url.current = window.location.href;

          setUrl(window.location.href);
        }
      });
    });

    const config = {
      childList: true,
      subtree: true
    };

    observer.observe(window.document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);
  

  useEffect(() => {
    if (!user) return;
    checkExtensionStatus();
  }, [extensionId, user?._id])

  useEffect(() => {
    setGraphqlQueryIds();
  }, [])

  useEffect(() => {
    // analytic 
    analytic({
      event: 'virtualPageview',
      pageUrl: getPageUrl(),
      referrer: refs.previousUrl.current,
      pageState: '',
      pageTitle: getPageTitle()
    });

    // Persist previous path for paywall/disabled analytics
    try {
      const prev = refs.previousUrl.current ? new URL(refs.previousUrl.current).pathname : '';
      if (prev) sessionStorage.setItem('ld_prev_path', prev);
    } catch {}

    // Previous url 
    refs.previousUrl.current = url;
  }, [url]);

  useEffect(() => {
    const crispEnabled = user?.preferences?.crispChat !== false;
    if (crispEnabled) crispShow();
    else crispHide();
    setCrispChat(crispEnabled);
  }, [user?.preferences?.crispChat])

  useEffect(() => {
    if (user) {
      const name =
        `${(user as any)?.firstName ?? (user as any)?.linkedinData?.firstName ?? ''} ${(user as any)?.lastName ?? (user as any)?.linkedinData?.lastName ?? ''}`.trim() ||
        (user as any)?.linkedinData?.publicIdentifier ||
        user.email;

      crispIdentify({
        email: user.email,
        name,
        id: user._id,
        data: {
          plan: (user as any)?.plan,
          workspace_id: workspace?._id,
          workspace_name: workspace?.name,
          subscription_id: subscription?._id,
          subscription_status: (subscription as any)?.status,
          subscription_interval: (subscription as any)?.interval,
        },
      });
    } else {
      crispReset();
    }
  }, [user, workspace?.name, (subscription as any)?.status, (subscription as any)?.interval]);


  useEffect(() => {
    if (user) {
      posthog.identify(user._id, {
        email: user.email,
        name: user.firstName + ' ' + user.lastName,
        workspaceName: workspace?.name,
        subscriptionStatus: subscription?.status,
        subscriptionId: subscription?.subscriptionId,
      });
    } else {
      posthog.reset();
    }
  }, [user]);

  const handlePaymentVerificationStatusChange = (needed: boolean, verificationData?: any) => {
    setShowPaymentVerificationBanner(needed);
    if (verificationData) {
      setPaymentVerificationData(verificationData);
    }
  };

  const handleDismissPaymentVerification = () => {
    setShowPaymentVerificationBanner(false);
    setPaymentVerificationData(null);
  };

  // NOTE: If we need CountdownPopup we need to enable useEffect()
  // const countdownPopupShowDelayHours = 6;

  // useEffect(() => {
  //   /* This effect sets a timestamp countdownPopupShowDelayHours in the future on first login/refresh,
  //      and shows the popup only after that time has passed,
  //      unless the state is already 'disabled'. */
  //   if (!(trial && onboardingStep < 0)) return;
  //   if (user) {
  //     const state = localStorage.getItem('countdownPopupState');
  //     let showAt = localStorage.getItem('countdownPopupShowAt');
  //     if (!showAt) {
  //       // First login/refresh: set showAt to now + countdownPopupShowDelayHours
  //       showAt = moment().add(countdownPopupShowDelayHours, 'hours').toISOString();
  //       localStorage.setItem('countdownPopupShowAt', showAt);
  //       if (state !== 'disabled') {
  //         localStorage.setItem('countdownPopupState', 'hidden');
  //       }
  //     } else {
  //       // Check if now is after showAt
  //       if (moment().isAfter(moment(showAt))) {
  //         if (state !== 'disabled') {
  //           localStorage.setItem('countdownPopupState', 'seen');
  //         }
  //       } else {
  //         if (state !== 'disabled') {
  //           localStorage.setItem('countdownPopupState', 'hidden');
  //         }
  //       }
  //     }
  //   }
  // }, [user]);

  const lastSyncAtLocalStorage = window.localStorage.getItem('lastSyncAt')
  const lastSyncAt = lastSyncAtLocalStorage && lastSyncAtLocalStorage !== 'undefined' ? lastSyncAtLocalStorage : 0

  const syncConnectionsStartedLocalStorage = window.localStorage.getItem('syncConnectionsStarted')
  const syncConnectionsStarted = syncConnectionsStartedLocalStorage && syncConnectionsStartedLocalStorage !== 'undefined' ? true : false

  const isTrial = trial && onboardingStep < 0;
  const isExpiredWorkspaceOwner = disablePremiumFeatures && workspaceOwner;
  const isActiveMonthlySubscriber = subscription?.interval === 'month' && subscription?.status === 'active';
  const isLifetime = lifetime;
  const showHomeBanner = (isHome || isConnections) && workspaceOwner && (isTrial || isActiveMonthlySubscriber || isLifetime);
  const showDisabledBanner = isDisabled && isExpiredWorkspaceOwner;

  const cardExpiryBannerState = useMemo(() => {
    if (!user) return null;
    const methods = mergePaymentMethodsForBanner(subscription, workspace);
    return getCardExpiryBannerState(methods);
  }, [user, subscription, workspace]);

  const canManageBilling = workspaceOwner || workspaceAdmin;
  const canManageBillingForCardExpiry = canManageBilling;
  /** Global strip: show on every route when card issue applies (billing pages included). */
  const showCardExpiryBanner = !!cardExpiryBannerState && canManageBillingForCardExpiry && !showPaymentVerificationBanner;

  // A held plan/seat change awaiting payment confirmation on an otherwise-active
  // subscription. Non-blocking - Stripe auto-discards it if not confirmed in time.
  const pendingUpdateExpiresAt = (subscription as any)?.pendingUpdateExpiresAt;
  const showPendingPurchaseBanner = !isOnboarding && canManageBilling && !showPaymentVerificationBanner && !!pendingUpdateExpiresAt && new Date(pendingUpdateExpiresAt).getTime() > Date.now();

  const LDBanners: {
    component: ReactNode,
    backgroundColor?: string,
    visible?: boolean,
    isComponent?: boolean,
    height?: number
  } [] = [
      {
          component: <PaymentVerificationBanner
            onDismiss={handleDismissPaymentVerification}
            verificationData={paymentVerificationData}
          />,
          isComponent: true,
          height: 68,
          visible: !isOnboarding && showPaymentVerificationBanner && canManageBilling
      },
      {
          component: <PaymentVerificationBanner
            variant="pending"
            onDismiss={handleDismissPaymentVerification}
          />,
          isComponent: true,
          height: 68,
          visible: showPendingPurchaseBanner
      },
      {
          component: <Row alignItems gap='10px'>
              <SvgFragment type='warning'/>
              <BannerText>{`Your trial ends ${moment().to(moment(subscription?.periodEnd * 1000))}.`} {`Save 30% by subscribing to a `}
                <Link to={AppRoutes.BILLING} style={{ textDecoration: 'none' }}> 
                  <Text>{'Yearly plan now'}</Text> 
                </Link>{` (grandfathered in forever).`}
              </BannerText>
          </Row>,
          visible: !isOnboarding && isTrial //isTrial
      },
      {
        component: <Row alignItems gap='10px'>
            <SvgFragment type='warning'/>
            <BannerText>{`Your trial ends ${moment().to(moment(subscription?.periodEnd * 1000))}.`} {`Book a personalized 15-min orientation to unlock your first wins. `}
              <UnderlineActionText onClick={scheduleMeeting}>Schedule Now.</UnderlineActionText>
            </BannerText>
        </Row>,
        visible: false //isTrial
      },
      {
          component: <Row alignItems gap='10px'>
              <LoadingSpinner />
              <Text $white>{`Syncing your connections... In the meantime, why not start creating tags?`} <ActionText invertedVariable onClickHandler={() =>openSyncMessage()}>Learn more.</ActionText></Text>
          </Row>,
          backgroundColor: styles.colors.primary600,
          visible: !isOnboarding && (!workspace?.initialSync && moment().isBefore(moment(lastSyncAt).add(20, 'minutes')) || syncConnectionsStarted)
      }
      ,
      {
          component: <Row alignItems gap='10px'>
              <BannerText $white>{`Please update your LeadDelta Chrome Extension to the latest version. Find step-by-step `} 
                <ActionText invertedVariable onClickHandler={() =>updateLeadDeltaExtension()}>instructions here.</ActionText>
              </BannerText>
          </Row>,
          backgroundColor: styles.colors.primary600,
          visible: !isOnboarding && extensionInstalled && extensionVersion !== process.env.REACT_APP_VERSION 
      },
      {
        component: <Row alignItems gap='10px'>
          <Link to={AppRoutes.BILLING} style={{ textDecoration: 'none', fontWeight: 'normal' }}> <Text $white><b>BlackFriday:</b> Get extra <b>20% OFF!</b> 🎉 Select yearly plan and enjoy total of <b>50%</b> savings on your first year. Use <b>BF24</b> at the checkout (new users only). </Text></Link>
        </Row>,
        backgroundColor: 'black',
        visible: false, /* isTrial || isExpiredWorkspaceOwner */ /* Banner for trials and expired trials (workspace owner)  */
      },
      {
        component: <BannerCountdown />,
        isComponent: true,
        height: 80,
        visible: false
        /* (isTrial || isExpiredWorkspaceOwner || isActiveMonthlySubscriber) && !isBilling  */
        /* Display on trial, expired workspace owners, or active monthly subscribers when off the billing page */
      },
      {
        component: <BlackFridayBanner />,
        isComponent: true,
        height: 120,
         visible: false
         /* showHomeBanner: workspace owner on home page, being trial, active monthly, or lifetime */
         /* showDisabledBanner: expired workspace owner on disabled page */
      },
      {
        component: <CardExpiryBanner state={cardExpiryBannerState} />,
        isComponent: true,
        height: 40,
        visible: !isOnboarding && cardExpiryBannerState && showCardExpiryBanner
      },
      // {
      //   component: <EOYbanner />,
      //   isComponent: true,
      //   height: 68,
      //    visible: (isTrial || showDisabledBanner) && !isBilling
      // }

  ];

  const isFullPageAuthRoute = (url: string) =>
    url?.includes(AppRoutes.SIGNIN) ||
    url?.includes(AppRoutes.SIGNUP) ||
    url?.includes(AppRoutes.FORGOT_PASSWORD) ||
    url?.includes(AppRoutes.SSO_CALLBACK) ||
    url?.includes(AppRoutes.ONBOARDING) ||
    url?.includes(AppRoutes.PROCESSING) ||
    url?.includes(AppRoutes.INVITE) ||
    url?.includes('/signup/verify-email');

  const showHeader = (url: string) => !isFullPageAuthRoute(url);

  // Inner component to handle sidebar closing on route change
  const AppContent: FunctionComponent = () => {
    const location = useLocation();
    const { setSidebar } = useAppActions();
    const { store: { sidebar } } = useAppStore();
    const previousPathnameRef = React.useRef<string>(location.pathname);

    useEffect(() => {
      // Close all sidebars when pathname changes
      if (previousPathnameRef.current !== location.pathname && sidebar.length > 0) {
        setSidebar([]);
      }
      previousPathnameRef.current = location.pathname;
    }, [location.pathname, sidebar.length, setSidebar]);

    return null;
  };

  return (
    <ThemeProvider theme={themes[darkMode ? 'dark' : 'light']}>
      <Reset />

      <BrowserRouter>
        <AppContent />
        <PaymentVerificationHandler onVerificationStatusChange={handlePaymentVerificationStatusChange} />
        <InboxStoreProvider>
        <AppContainer>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {user && !url?.includes(AppRoutes.DELETE_USER_CONFIRMATION) && (
              <StyledBannerBox>
                {LDBanners?.map((LDBanner: any) =>
                  LDBanner.visible && (
                    <Banner backgroundColor={LDBanner.backgroundColor} isComponent={LDBanner.isComponent}>
                      {LDBanner.component}
                    </Banner>
                  )
                )}
              </StyledBannerBox>
            )}
            <div style={{ display: 'flex', flex: 1, minHeight: 0, width: '100%' }}>
              {user && showHeader(url) && (
                <SideNav campaignsAnnouncementEnabled={hideDemoVideo} />
              )}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
              {!hideDemoVideo && !isOnboarding && <DemoVideo onClick={setHideDemoVideo} />}
              {/* {(user && localStorage.getItem('countdownPopupState') === 'seen' && trial && onboardingStep < 0) && <CountdownPopup />} */}
            {!!sidebar.length  && sidebar.map((sidebarItem: ISidebar) =>
                  <Sidebar
                    zIndex={sidebarItem.zIndex}
                    sidebarWidth={
                      sidebarItem.type === SidebarTypes.CONNECTION_NETWORK ? 450
                      : sidebarItem.type === SidebarTypes.CONNECTION_FILTER ? 672
                      : undefined
                    }
                    $flush={sidebarItem.type === SidebarTypes.CONNECTION_FILTER}
                    onClose={() => {
                    if(typeof sidebarItem.type === 'string' && ['connectionActions'].includes(sidebarItem.type)) hideModal()
                    closeSidebar(sidebarItem.type)
                  }}>
                    <SidebarRender key={sidebarItem.zIndex} sidebarType={sidebarItem.type} {...sidebarItem.props}/>
                  </Sidebar>)}
              {showHeader(url) && <Header />}
                <PagesContainer url={url}>
                  <AppRouter />
                </PagesContainer>
              </div>
            </div>
          </div>
          <AppModal />
          <Toast />
          <Spinner showHideSpinner={spinner} />
          {crispChat && <CrispChat />}
          <ConnectLinkedInProfileModal />
          <ChatWindows />
          {user && !isOnboarding && <WebUpdateBanner />}
        </AppContainer>
        </InboxStoreProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

const PagesContainer = styled.div<{ url: string }>`
    width: 100%;
    height: ${({ url }) => {
        const isFullPageAuthRoute =
            url?.includes(AppRoutes.SIGNIN) ||
            url?.includes(AppRoutes.SIGNUP) ||
            url?.includes(AppRoutes.FORGOT_PASSWORD) ||
            url?.includes(AppRoutes.SSO_CALLBACK) ||
            url?.includes(AppRoutes.ONBOARDING) ||
            url?.includes(AppRoutes.PROCESSING) ||
            url?.includes(AppRoutes.INVITE) ||
            url?.includes('/signup/verify-email');

        if (isFullPageAuthRoute) {
            return '100%';
        }
        return `calc(100% - ${styles.lengths.navbarHeight})`;
    }};
    overflow-y: auto;
`
const StyledBannerBox = styled(Column)`
    width: 100%;
`
const BannerText = styled(Text)`
    ${({ $white }) => $white && `color: ${styles.colors.white};`}
    @media (max-width: 430px) {
        display: inline;
        a, div {
          display: inline-block;
        }
    }
`
const UnderlineActionText = styled.span`
    cursor: pointer;
    
    color: ${styles.colors.black600};

    &:hover {
        color: ${styles.colors.primary600};
    }
`

function App() {
  return MAINTENANCE_MODE ? <MaintenanceShell /> : <AppMain />;
}

export default App;
